/** 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR) — Core owns the workspace boundary. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { getAppApiBaseUrl } from '../utils/api-same-origin.utils';
import { getApplicationCodeForApi, getPlatformAppsUrl } from '../config/app.config';
import { useNativeAuthRequestState } from '../components/auth/NativeAuthConsent';
import { FormModalSurface } from '../components/shared/FormModalSurface';
import { getWorkspaceCopy } from '../components/auth/workspace-copy';
import { readWorkspaceEntryIntent, workspaceProductRoot } from './workspace-navigation.utils';
import { WORKSPACE_CHANNEL, WORKSPACE_EPOCH_KEY, WORKSPACE_CONTEXT_CHANGED_EVENT, WORKSPACE_SIGN_OUT_EVENT, parseWorkspaceNotice, publishWorkspaceNotice, readWorkspaceNotice } from './workspace-transition.utils';
import '../components/auth/WorkspaceChooser.css';

export interface TenantWorkspace {
  membershipId: string; customerId: string; customerName: string; customerCode?: string | null;
  membershipType: 'OWNER' | 'INTERNAL' | 'EXTERNAL'; tenantRole: 'OWNER' | 'TENANTADMIN' | 'USER';
  status: 'ACTIVE'; isSelected: boolean;
}
interface TenantAuthBridge {
  user: { membershipId?: string | null } | null; isInitialized: boolean; language: string;
  workspaceSelectionRequired: boolean; resetForWorkspaceSwitch: () => void;
  logout?: () => void | Promise<void>;
}
interface TenantContextValue {
  nativeAuthRequest: string | null; clearNativeAuthRequest: () => void;
  workspaces: TenantWorkspace[]; currentWorkspace: TenantWorkspace | null; totalCount: number;
  canSwitchWorkspace: boolean; openWorkspaceChooser: () => void; closeWorkspaceChooser: () => void;
  selectWorkspace: (membershipId: string) => Promise<void>;
}
const TenantContext = createContext<TenantContextValue | undefined>(undefined);
export function useTenant() { const value = useContext(TenantContext); if (!value) throw new Error('useTenant requires TenantProvider'); return value; }
export function useOptionalTenant() { return useContext(TenantContext); }
export interface TenantProviderProps { children: React.ReactNode; auth: TenantAuthBridge; workspaceControlPlaneUrl?: string; }

export function TenantProvider({ children, auth }: TenantProviderProps) {
  const { requestId: nativeAuthRequest, clear: clearNativeAuthRequest } = useNativeAuthRequestState();
  const copy = getWorkspaceCopy(auth.language);
  const appCode = getApplicationCodeForApi();
  const entry = useMemo(() => readWorkspaceEntryIntent(window.location.href, appCode), [appCode]);
  const centralEntry = Boolean(entry && /\/login\/?$/.test(window.location.pathname));
  const automaticEntry = centralEntry && new URLSearchParams(window.location.search).get('account') !== 'change';
  const productRoot = workspaceProductRoot(import.meta.env.VITE_BASE_PATH || '');
  const root = centralEntry && entry ? entry.root : productRoot;
  const api = getAppApiBaseUrl();
  const [open, setOpen] = useState(false), [entryActive, setEntryActive] = useState(false);
  const [entryChecked, setEntryChecked] = useState(false);
  const [workspaces, setWorkspaces] = useState<TenantWorkspace[]>([]);
  const [totalCount, setTotalCount] = useState(0), [page, setPage] = useState(1);
  const [search, setSearch] = useState(''), [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false), [loadError, setLoadError] = useState(false), [reload, setReload] = useState(0);
  const [switchable, setSwitchable] = useState<string | null>(null), [candidate, setCandidate] = useState<TenantWorkspace | null>(null);
  const [phase, setPhase] = useState<'clearing' | 'verifying' | 'opening' | 'error' | null>(() => parseWorkspaceNotice(readWorkspaceNotice())?.phase === 'begin' ? 'clearing' : null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [signedOut, setSignedOut] = useState(() => parseWorkspaceNotice(readWorkspaceNotice())?.phase === 'signed-out');
  const signedOutRef = useRef(signedOut), generation = useRef(0), pending = useRef(new Set<AbortController>());
  const changing = useRef(false), reset = useRef(auth.resetForWorkspaceSwitch);
  reset.current = auth.resetForWorkspaceSwitch;
  const channel = useRef<BroadcastChannel | null>(null), notice = useRef(readWorkspaceNotice()), ownEpoch = useRef<string | null>(null);
  const expectedMembership = useRef<string | null>(null);
  const clearNative = useRef(clearNativeAuthRequest); clearNative.current = clearNativeAuthRequest;
  const currentWorkspace = workspaces.find(item => item.isSelected) ?? null;
  const required = !signedOut && (auth.workspaceSelectionRequired || entryActive || (automaticEntry && !entryChecked));
  const endTransition = useCallback(() => {
    signedOutRef.current = true; generation.current += 1;
    pending.current.forEach(controller => controller.abort()); pending.current.clear();
    changing.current = false; ownEpoch.current = null; expectedMembership.current = null;
    setSignedOut(true); setPhase(null); setOpen(false); setCandidate(null); setEntryActive(false);
    setAccessDenied(false);
    setEntryChecked(true); setWorkspaces([]); setTotalCount(0); setSwitchable(null); setLoading(false); setLoadError(false);
    clearNative.current();
  }, []);
  useEffect(() => {
    if (auth.isInitialized && (auth.workspaceSelectionRequired || auth.user?.membershipId)) {
      signedOutRef.current = false; setSignedOut(false);
    }
  }, [auth.isInitialized, auth.workspaceSelectionRequired, auth.user?.membershipId]);
  const navigateRoot = useCallback(() => window.location.replace(nativeAuthRequest
    ? `${root === '/' ? '' : root}/login#native_request=${nativeAuthRequest}` : root), [root, nativeAuthRequest]);
  const verify = useCallback(async (expected?: TenantWorkspace) => {
    if (signedOutRef.current) return;
    const current = generation.current, controller = new AbortController(); pending.current.add(controller);
    setAccessDenied(false); setPhase('verifying');
    try {
      const response = await fetch(`${api}/api/auth/workspace-context`, { credentials:'include', cache:'no-store', signal:AbortSignal.any([controller.signal, AbortSignal.timeout(20000)]) });
      const body = await response.json();
      if (signedOutRef.current || current !== generation.current || controller.signal.aborted) return;
      if (!response.ok || body?.success !== true || !body.data?.membershipId
        || (expected && (body.data.membershipId !== expected.membershipId || body.data.customerId !== expected.customerId))) throw new Error();
      const marker = parseWorkspaceNotice(readWorkspaceNotice());
      if (marker?.phase === 'begin') publishWorkspaceNotice({ epoch:marker.epoch, phase:'settled' }, channel.current);
      setPhase('opening'); navigateRoot();
    } catch { if (!signedOutRef.current && current === generation.current) setPhase('error'); }
    finally { pending.current.delete(controller); }
  }, [api, navigateRoot]);

  useEffect(() => {
    let recovery: ReturnType<typeof setTimeout> | undefined;
    const seen = (value: unknown) => {
      const parsed = parseWorkspaceNotice(value);
      if (parsed?.phase === 'signed-out') { if (recovery) clearTimeout(recovery); endTransition(); reset.current(); return; }
      if (signedOutRef.current) return;
      if (!parsed || parsed.epoch === ownEpoch.current || value === notice.current) return;
      notice.current = String(value); changing.current = true; setPhase('clearing'); reset.current();
      if (recovery) clearTimeout(recovery);
      if (parsed.phase === 'settled') void verify();
      else recovery = setTimeout(() => void verify(), 30000);
    };
    if (typeof BroadcastChannel !== 'undefined') { channel.current = new BroadcastChannel(WORKSPACE_CHANNEL); channel.current.onmessage = event => seen(event.data); }
    const storage = (event: StorageEvent) => { if (event.key === WORKSPACE_EPOCH_KEY) seen(event.newValue); };
    const restored = (event: Event) => {
      if (signedOutRef.current) return;
      if (event.type === 'pageshow' && (event as PageTransitionEvent).persisted) {
        changing.current = true; setPhase('clearing'); reset.current(); void verify();
      } else seen(readWorkspaceNotice());
    };
    const mismatch = () => { if (signedOutRef.current) return; changing.current = true; setPhase('clearing'); reset.current(); void verify(); };
    const signOut = () => { if (recovery) clearTimeout(recovery); endTransition(); };
    window.addEventListener('storage', storage); window.addEventListener('pageshow', restored);
    window.addEventListener(WORKSPACE_SIGN_OUT_EVENT, signOut);
    document.addEventListener('visibilitychange', restored); window.addEventListener(WORKSPACE_CONTEXT_CHANGED_EVENT, mismatch);
    const initial = readWorkspaceNotice();
    if (parseWorkspaceNotice(initial)?.phase === 'begin') { notice.current = null; seen(initial); }
    return () => { if (recovery) clearTimeout(recovery); channel.current?.close(); channel.current = null;
      window.removeEventListener('storage', storage); window.removeEventListener('pageshow', restored);
      window.removeEventListener(WORKSPACE_SIGN_OUT_EVENT, signOut); pending.current.forEach(controller => controller.abort());
      document.removeEventListener('visibilitychange', restored); window.removeEventListener(WORKSPACE_CONTEXT_CHANGED_EVENT, mismatch); };
  }, [verify, endTransition]);

  useEffect(() => {
    if (signedOut || !auth.isInitialized || !auth.user?.membershipId || phase) { setSwitchable(null); return; }
    const controller = new AbortController(), membership = String(auth.user.membershipId);
    void fetch(`${api}/api/auth/workspace-capability`, { credentials:'include', cache:'no-store', signal:controller.signal })
      .then(response => response.ok ? response.json() : null)
      .then(body => { if (!controller.signal.aborted && !signedOutRef.current) setSwitchable(body?.data?.canSwitchWorkspace === true ? membership : null); })
      .catch(() => { if (!controller.signal.aborted) setSwitchable(null); });
    return () => controller.abort();
  }, [api, auth.isInitialized, auth.user?.membershipId, phase, signedOut]);

  useEffect(() => {
    if (signedOut || !auth.isInitialized || phase || !(open || required || automaticEntry)) return;
    const controller = new AbortController(); setLoading(true); setLoadError(false);
    const params = new URLSearchParams({ page:String(page), pageSize:'20', search:query });
    void fetch(`${api}/api/auth/workspaces?${params}`, { credentials:'include', cache:'no-store', signal:controller.signal })
      .then(async response => {
        const body = await response.json();
        if (controller.signal.aborted || signedOutRef.current) return;
        if (!response.ok || body?.success !== true || !Array.isArray(body.data?.items)) {
          if (centralEntry && !auth.workspaceSelectionRequired && !entryActive && !open && response.status === 401) { setEntryChecked(true); return; }
          throw new Error();
        }
        const items = body.data.items as TenantWorkspace[];
        setWorkspaces(items); setTotalCount(Number(body.data.totalCount) || 0);
        setEntryChecked(true);
        if (page === 1 && !query) expectedMembership.current = body.data.currentMembershipId ?? items.find(item => item.isSelected)?.membershipId ?? auth.user?.membershipId ?? null;
        if (centralEntry) setEntryActive(true);
      }).catch(() => { if (!controller.signal.aborted && !signedOutRef.current) setLoadError(true); })
      .finally(() => { if (!controller.signal.aborted && !signedOutRef.current) setLoading(false); });
    return () => controller.abort();
  }, [api, auth.isInitialized, auth.user?.membershipId, auth.workspaceSelectionRequired, entryActive, centralEntry, automaticEntry, open, required, page, query, reload, phase, signedOut]);

  const selectWorkspace = useCallback(async (membershipId: string) => {
    const chosen = workspaces.find(item => item.membershipId === membershipId);
    if (!chosen || changing.current || signedOutRef.current) return;
    const current = generation.current;
    const operation = async () => {
      if (changing.current || signedOutRef.current || current !== generation.current) return;
      const controller = new AbortController(); pending.current.add(controller);
      setAccessDenied(false);
      changing.current = true;
      const epoch = crypto.randomUUID(); ownEpoch.current = epoch;
      publishWorkspaceNotice({ epoch, phase:'begin' }, channel.current);
      // Dispose dirty-form listeners and the old provider tree before rotating the cookie.
      flushSync(() => { setCandidate(null); setPhase('clearing'); reset.current(); });
      try {
        const response = await fetch(`${api}/api/auth/workspaces/select`, { method:'POST', credentials:'include', cache:'no-store',
          headers:{ 'Content-Type':'application/json' }, signal:AbortSignal.any([controller.signal, AbortSignal.timeout(25000)]),
          body:JSON.stringify({ membershipId, expectedMembershipId:expectedMembership.current,
            ...(centralEntry && entry ? { applicationCode:entry.applicationCode } : {}) }) });
        const body = await response.json();
        if (signedOutRef.current || current !== generation.current || controller.signal.aborted) return;
        if (response.status === 403 && body?.code === 'WORKSPACE_APPLICATION_ACCESS_DENIED') {
          throw Object.assign(new Error(), { code: 'WORKSPACE_APPLICATION_ACCESS_DENIED' });
        }
        if (!response.ok || body?.success !== true || body.data?.workspace?.membershipId !== membershipId) throw new Error();
        publishWorkspaceNotice({ epoch, phase:'settled' }, channel.current);
        if (centralEntry && entry && entry.applicationCode !== appCode) { setPhase('opening'); navigateRoot(); }
        else await verify(chosen);
      } catch (error) {
        if (signedOutRef.current || current !== generation.current) return;
        setAccessDenied((error as { code?: string })?.code === 'WORKSPACE_APPLICATION_ACCESS_DENIED');
        // Response loss may still mean a committed session change. Never remount old state.
        publishWorkspaceNotice({ epoch, phase:'settled' }, channel.current); setPhase('error');
      } finally { pending.current.delete(controller); }
    };
    if (navigator.locks) await navigator.locks.request('qubepro-workspace-switch', { ifAvailable:true }, async lock => { if (lock) await operation(); else if (!signedOutRef.current && current === generation.current) setLoadError(true); });
    else await operation();
  }, [workspaces, api, centralEntry, entry, appCode, navigateRoot, verify]);

  useEffect(() => {
    if (required && !loading && !loadError && totalCount === 1 && workspaces.length === 1) void selectWorkspace(workspaces[0].membershipId);
  }, [required, loading, loadError, totalCount, workspaces, selectWorkspace]);

  const value = useMemo<TenantContextValue>(() => ({
    nativeAuthRequest, clearNativeAuthRequest, workspaces, currentWorkspace, totalCount,
    canSwitchWorkspace:Boolean(auth.user?.membershipId && auth.user.membershipId === switchable && !phase),
    openWorkspaceChooser:() => { if (signedOutRef.current) return; setPage(1); setSearch(''); setQuery(''); setOpen(true); },
    closeWorkspaceChooser:() => { if (!required) { setOpen(false); setCandidate(null); } }, selectWorkspace,
  }), [nativeAuthRequest, clearNativeAuthRequest, workspaces, currentWorkspace, totalCount, auth.user?.membershipId, switchable, phase, required, selectWorkspace]);
  const returnToLogin = () => auth.logout ? void auth.logout() : window.location.replace(`${productRoot === '/' ? '' : productRoot}/login`);
  const returnToApps = () => {
    const target = new URL(getPlatformAppsUrl(), window.location.origin);
    if (target.origin === window.location.origin) window.location.replace(`${target.origin}${target.pathname}`);
    else returnToLogin();
  };
  return <TenantContext.Provider value={value}>
    {phase ? <div className="workspace-chooser-backdrop"><section className="workspace-chooser" aria-busy={phase !== 'error'} aria-live="polite" dir={auth.language === 'ar' ? 'rtl' : 'ltr'}>
      <h1>{accessDenied ? copy.accessDeniedTitle : copy.switching}</h1>{phase === 'error' ? <><p role="alert">{accessDenied ? copy.accessDenied : copy.switchError}</p>
        {accessDenied ? <button type="button" className="btn-primary" onClick={returnToApps}>{copy.backToApps}</button> : <>
          <button type="button" className="btn-primary" onClick={() => void verify()}>{copy.retry}</button>
          <button type="button" className="btn-secondary" onClick={returnToLogin}>{copy.login}</button></>}</>
      : <><span className="workspace-chooser-spinner" aria-hidden="true" /><p role="status">{phase === 'clearing' ? copy.clearing : phase === 'verifying' ? copy.verifying : copy.opening}</p></>}
      {auth.logout && <button type="button" className="btn-secondary" onClick={() => void auth.logout?.()}>{copy.signOut}</button>}
    </section></div> : <>
      {!required && children}
      <FormModalSurface isOpen={open || required} title={copy.title} size="md" surfaceScope="viewport" maximizable={false} onClose={() => { if (!required) setOpen(false); }}
        footer={required && auth.logout ? <button type="button" className="btn-secondary" onClick={() => void auth.logout?.()}>{copy.signOut}</button> : undefined}>
        <div className="workspace-chooser-body" dir={auth.language === 'ar' ? 'rtl' : 'ltr'}>
          <p>{copy.description}</p>
          <form className="workspace-chooser-search" onSubmit={event => { event.preventDefault(); setPage(1); setQuery(search.trim()); }}>
            <input type="search" aria-label={copy.search} placeholder={copy.search} value={search} onChange={event => setSearch(event.target.value)} />
            <button type="submit" className="btn-primary" disabled={loading}>{copy.find}</button>
          </form>
          {loading ? <div className="workspace-chooser-state" role="status">{copy.loading}</div> : loadError
            ? <div className="workspace-chooser-error" role="alert">{copy.loadError}<button type="button" className="btn-secondary" onClick={() => setReload(v => v + 1)}>{copy.retry}</button>
              <button type="button" className="btn-secondary" onClick={returnToLogin}>{copy.login}</button></div>
            : <div className="workspace-chooser-list">{workspaces.length ? workspaces.map(workspace => <button type="button" key={workspace.membershipId}
              className={`workspace-chooser-item${workspace.isSelected ? ' is-selected' : ''}`}
              onClick={() => { if (required) void selectWorkspace(workspace.membershipId); else if (!workspace.isSelected) setCandidate(workspace); else setOpen(false); }}>
              <span className="workspace-chooser-monogram" aria-hidden="true">{workspace.customerName.slice(0,1)}</span>
              <span className="workspace-chooser-item-copy"><strong>{workspace.customerName}</strong></span>
              {workspace.isSelected && <span className="workspace-chooser-item-status">{copy.current}</span>}
            </button>) : <p>{copy.empty}</p>}</div>}
          <nav className="workspace-chooser-pagination" aria-label={copy.title}>
            <button type="button" className="btn-secondary" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>{copy.previous}</button>
            <span>{page} / {Math.max(1, Math.ceil(totalCount / 20))}</span>
            <button type="button" className="btn-secondary" disabled={page * 20 >= totalCount || loading} onClick={() => setPage(p => p + 1)}>{copy.next}</button>
          </nav>
        </div>
      </FormModalSurface>
      <FormModalSurface isOpen={Boolean(candidate)} title={copy.confirm} size="sm" surfaceScope="viewport" maximizable={false} onClose={() => setCandidate(null)} footer={<>
        <button type="button" className="btn-secondary" onClick={() => setCandidate(null)}>{copy.cancel}</button>
        <button type="button" className="btn-primary" onClick={() => candidate && void selectWorkspace(candidate.membershipId)}>{copy.switch}</button>
      </>}><p><strong>{candidate?.customerName}</strong></p><p>{copy.leave}</p></FormModalSurface>
    </>}
  </TenantContext.Provider>;
}
